import { Howl } from "howler";
import { ToastSoundEffectId } from "./toastSound";

/**
 * Plays a fixed, self-hosted catalog of toast sound effects (`public/sounds/
 * <id>.mp3`) directly via howler, bypassing react-sounds' `playSound`/
 * `preloadSounds`.
 *
 * react-sounds probes `/sounds/<id>.mp3` with a 300ms-abort HEAD request and
 * falls back to a CDN bucket that no longer exists (WEBV3-352) when that
 * probe loses the race. Worse, it caches the resulting rejection at module
 * scope for the page's life, so one slow probe silences every later ding
 * until a hard refresh. Every id we ever ask for already ships in public/
 * (enforced by toastSound.asset.test.ts), so there is nothing to probe or
 * fall back from — fetch the local file directly and, on failure, drop the
 * cache entry so the next attempt gets a fresh load instead of a poisoned one.
 */

type LoadEntry = { howl: Howl; loaded: Promise<void> };

const cache = new Map<ToastSoundEffectId, LoadEntry>();

function load(id: ToastSoundEffectId): LoadEntry {
  const existing = cache.get(id);
  if (existing) return existing;

  let resolveLoaded!: () => void;
  let rejectLoaded!: (error: Error) => void;
  const loaded = new Promise<void>((resolve, reject) => {
    resolveLoaded = resolve;
    rejectLoaded = reject;
  });

  const howl = new Howl({
    src: [`/sounds/${id}.mp3`],
    preload: true,
    onload: () => resolveLoaded(),
    onloaderror: (_soundId, error) => {
      // Don't leave a rejected promise sitting in the cache forever — the
      // whole point of dropping react-sounds is that a bad load must not
      // silence every later attempt.
      cache.delete(id);
      howl.unload();
      rejectLoaded(new Error(`Failed to load sound "${id}": ${String(error)}`));
    },
  });

  const entry: LoadEntry = { howl, loaded };
  cache.set(id, entry);
  return entry;
}

/**
 * Loads one effect and requests playback. Resolves once the sound has loaded
 * and `play()` has been called (howler may still queue actual audio output,
 * e.g. until the AudioContext unlocks); rejects if the file fails to load
 * (network error, missing file).
 * @param {ToastSoundEffectId} id - The catalog effect to play.
 * @returns {Promise<void>}
 */
export async function playSound(id: ToastSoundEffectId): Promise<void> {
  const entry = load(id);
  await entry.loaded;
  entry.howl.play();
}

/**
 * Warms one or more effects so a later `playSound` call doesn't pay the load
 * latency. Rejects if any of the requested sounds fails to load.
 * @param {ToastSoundEffectId[]} ids - The catalog effects to warm.
 * @returns {Promise<void>}
 */
export async function preloadSounds(ids: ToastSoundEffectId[]): Promise<void> {
  await Promise.all(ids.map((id) => load(id).loaded));
}
